import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import usePageSeo from "../../hooks/usePageSeo";
import useUtmParams from "../../hooks/useUtmParams";
import StartHeader from "../../components/start/StartHeader";
import StartFooter from "../../components/start/StartFooter";
import StartHero from "../../components/start/StartHero";
import StartProfessions from "../../components/start/StartProfessions";
import StartFeatureRows from "../../components/start/StartFeatureRow";
import StartStats from "../../components/start/StartStats";
import StartTestimonials from "../../components/start/StartTestimonials";
import StartSecurity from "../../components/start/StartSecurity";
import StartFaq from "../../components/start/StartFaq";
import StartClose from "../../components/start/StartClose";
import StartStickyBar from "../../components/start/StartStickyBar";
import { SEO, getHeroTitle, getProfession } from "../../data/startPageContent";
import { buildStartJsonLd } from "./startJsonLd";
import "../../components/start/startReveal.css";
import "./StartPage.css";

const JSONLD_ID = "start-jsonld";

export default function StartPage() {
  const location = useLocation();

  usePageSeo({ title: SEO.title, description: SEO.description, path: SEO.path });

  // Profession (`?p=`) and headline variant (`?v=`) from the URL.
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  // Profession state: seeded from `?p=`, changed by the chips, mirrored back
  // into the URL with replace (no history spam, deep-linkable).
  const navigate = useNavigate();
  // Hydration-safe: first render always matches the prerendered snapshot
  // (no profession / default headline); URL params are applied after mount.
  const [professionKey, setProfessionKey] = useState(null);
  const [heroVariant, setHeroVariant] = useState(null);
  useEffect(() => {
    const fromUrl = getProfession(query.get("p"))?.key || null;
    if (fromUrl) setProfessionKey(fromUrl);
    const v = query.get("v");
    if (v) setHeroVariant(v);
    // Only on mount — later changes come from the chips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleProfessionChange = useCallback(
    (key) => {
      setProfessionKey(key);
      const next = new URLSearchParams(location.search);
      if (key) next.set("p", key);
      else next.delete("p");
      const qs = next.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );
  const heroTitle = getHeroTitle(heroVariant);
  const utm = useUtmParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // JSON-LD inject / remove (pattern: ClinicManagementPage).
  useEffect(() => {
    try {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = JSONLD_ID;
      script.textContent = JSON.stringify(buildStartJsonLd());
      document.head.appendChild(script);
    } catch (error) {
      console.error("[CK-1058] StartPage JSON-LD inject failed", {
        errorCode: error?.code,
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
    }
    return () => {
      try {
        document.getElementById(JSONLD_ID)?.remove();
      } catch (error) {
        console.error("[CK-1058] StartPage JSON-LD remove failed", {
          errorCode: error?.code,
          errorMessage: error?.message,
          timestamp: new Date().toISOString(),
        });
      }
    };
  }, []);

  return (
    <div className="st-page" dir="rtl">
      <StartHeader />

      <main className="st-main" data-profession={professionKey || ""}>
        <StartHero title={heroTitle} profession={professionKey} utm={utm} />

        <StartFeatureRows profession={professionKey} />
        <StartStats />
        <StartTestimonials />
        <StartSecurity />
        <StartProfessions value={professionKey} onChange={handleProfessionChange} />
        <StartFaq />
        <StartClose profession={professionKey} utm={utm} />
      </main>

      <StartFooter />
      <StartStickyBar />
    </div>
  );
}
